/**
 * VerguetungPage – Vergütungsabrechnung nach VBVG
 */
import { useState, useEffect } from 'react';
import { Calculator, Download, Loader2, FileText, Euro } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const QUALIFIKATIONEN = [
  { value: 'ohne_ausbildung', label: 'Ohne Ausbildung (23,00 €/h)' },
  { value: 'abgeschlossene_ausbildung', label: 'Abgeschlossene Ausbildung (29,50 €/h)' },
  { value: 'hochschulabschluss', label: 'Hochschulabschluss (39,00 €/h)' },
];

export default function VerguetungPage() {
  const [qualifikation, setQualifikation] = useState('abgeschlossene_ausbildung');
  const [quartal, setQuartal] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    const now = new Date();
    const q = Math.ceil((now.getMonth() + 1) / 3);
    setQuartal(`${now.getFullYear()}-Q${q}`);
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/verguetung/list`);
      const data = await res.json();
      if (data.success) setHistory(data.abrechnungen || []);
    } catch {}
  };

  const handleBerechnen = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/verguetung/berechnen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quartal, qualifikation }),
      });
      const data = await res.json();
      if (data.success) {
        setResult(data);
        toast.success(`Vergütung berechnet: ${data.gesamt_betrag.toFixed(2)} €`);
        if (data.anzahl_klaerungsfaelle > 0) {
          toast.warning(`${data.anzahl_klaerungsfaelle} Betreuung(en) nicht abgerechnet: Betreuungsbeginn klären`);
        }
        loadHistory();
      } else {
        toast.error(data.error);
      }
    } catch { toast.error('Berechnung fehlgeschlagen'); }
    finally { setLoading(false); }
  };

  const handleExport = async (id) => {
    setExporting(id);
    try {
      const res = await fetch(`${API_URL}/api/verguetung/export-pdf/${id}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `Verguetung_${id}.pdf`; a.click();
        URL.revokeObjectURL(url);
        toast.success('PDF heruntergeladen');
      } else { toast.error('Export fehlgeschlagen'); }
    } catch { toast.error('Export fehlgeschlagen'); }
    finally { setExporting(null); }
  };

  const handleExportDocx = async (id) => {
    setExporting(id);
    try {
      const res = await fetch(`${API_URL}/api/verguetung/export-docx/${id}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `Verguetung_${id}.docx`; a.click();
        URL.revokeObjectURL(url);
        toast.success('Word-Dokument heruntergeladen');
      } else { toast.error('DOCX-Export fehlgeschlagen'); }
    } catch { toast.error('DOCX-Export fehlgeschlagen'); }
    finally { setExporting(null); }
  };


  const quartale = [];
  const now = new Date();
  for (let i = -4; i <= 1; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i * 3, 1);
    const q = Math.ceil((d.getMonth() + 1) / 3);
    quartale.push({ value: `${d.getFullYear()}-Q${q}`, label: `Q${q}/${d.getFullYear()}` });
  }
  const uniqueQuartale = [...new Map(quartale.map(q => [q.value, q])).values()];

  return (
    <div className="space-y-6" data-testid="verguetung-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Vergütungsabrechnung</h1>
        <p className="text-slate-500 mt-1">Automatische Berechnung nach VBVG</p>
      </div>

      {/* Berechnung */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="w-5 h-5 text-violet-500" />
            Quartalsabrechnung berechnen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="space-y-1">
              <label className="text-xs text-slate-600 font-medium">Quartal</label>
              <Select value={quartal} onValueChange={setQuartal}>
                <SelectTrigger className="w-[160px]" data-testid="quartal-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {uniqueQuartale.map(q => (
                    <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-600 font-medium">Qualifikation</label>
              <Select value={qualifikation} onValueChange={setQualifikation}>
                <SelectTrigger className="w-[280px]" data-testid="qualifikation-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUALIFIKATIONEN.map(q => (
                    <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleBerechnen} disabled={loading}
              className="bg-violet-600 hover:bg-violet-700" data-testid="berechnen-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
              Berechnen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Ergebnis */}
      {result && (
        <Card className="border-violet-200" data-testid="verguetung-result">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                Ergebnis {result.quartal}
              </CardTitle>
              {result.abrechnung_id && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleExportDocx(result.abrechnung_id)}
                    disabled={exporting === result.abrechnung_id} data-testid="export-docx-btn">
                    {exporting === result.abrechnung_id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                    Word (.docx)
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleExport(result.abrechnung_id)}
                    disabled={exporting === result.abrechnung_id} data-testid="export-pdf-btn">
                    <Download className="w-4 h-4 mr-1" />
                    PDF
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="bg-violet-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-violet-700">{result.gesamt_betrag?.toFixed(2)} €</p>
                <p className="text-xs text-violet-500">Gesamtbetrag</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-slate-700">{result.anzahl_faelle}</p>
                <p className="text-xs text-slate-500">Betreuungen</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-slate-700">{result.gesamt_stunden?.toFixed(1)}</p>
                <p className="text-xs text-slate-500">Stunden gesamt</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-slate-700">{result.stundensatz?.toFixed(2)} €</p>
                <p className="text-xs text-slate-500">Stundensatz</p>
              </div>
            </div>

            {result.klaerungsfaelle?.length > 0 && (
              <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="verguetung-klaerungsfaelle">
                <p className="text-sm font-semibold text-amber-800">
                  Nicht abgerechnet – Betreuungsbeginn klären ({result.klaerungsfaelle.length})
                </p>
                <p className="text-xs text-amber-700 mb-2">
                  Ohne belegten ursprünglichen Betreuungsbeginn wird keine Vergütungsdauer angenommen. Diese Fälle sind im Gesamtbetrag nicht enthalten.
                </p>
                <ul className="text-xs text-amber-900 space-y-0.5">
                  {result.klaerungsfaelle.map((k) => (
                    <li key={k.klient_id} data-testid={`verguetung-klaeren-${k.klient_id}`}>
                      <span className="font-medium">{k.klient_name}</span>
                      {k.aktenzeichen ? ` · ${k.aktenzeichen}` : ''} – {k.grund}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="max-h-[300px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b text-slate-500 text-xs">
                    <th className="text-left py-2 px-2">Betreuter</th>
                    <th className="text-left py-2">AZ</th>
                    <th className="text-left py-2">Wohnform</th>
                    <th className="text-right py-2">Stunden</th>
                    <th className="text-right py-2 px-2">Betrag</th>
                  </tr>
                </thead>
                <tbody>
                  {result.abrechnungen?.map((a, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-1.5 px-2 font-medium">{a.klient_name}</td>
                      <td className="py-1.5 text-xs text-slate-500">{a.aktenzeichen}</td>
                      <td className="py-1.5">
                        <Badge variant="outline" className="text-[10px]">{a.wohnform}</Badge>
                      </td>
                      <td className="py-1.5 text-right">{a.gesamt_stunden?.toFixed(1)}</td>
                      <td className="py-1.5 px-2 text-right font-medium">{a.betrag?.toFixed(2)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historie */}
      {history.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Frühere Abrechnungen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {history.map(h => (
                <div key={h.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium">{h.quartal}</p>
                      <p className="text-xs text-slate-500">{h.anzahl_faelle} Fälle · {h.gesamt_stunden?.toFixed(1)} Std
                        {h.anzahl_klaerungsfaelle > 0 && (
                          <span className="text-amber-700"> · {h.anzahl_klaerungsfaelle} Betreuungsbeginn klären</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-violet-700">{h.gesamt_betrag?.toFixed(2)} €</span>
                    <Button size="sm" variant="outline" onClick={() => handleExport(h.id)}
                      disabled={exporting === h.id}>
                      {exporting === h.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
